import { Injectable, Inject } from '@angular/core';
import { Observable, bindCallback, Subject } from 'rxjs';
import * as microsoftTeams from '@microsoft/teams-js';
import { WINDOW } from '@app/common/app-tokens';
import { shareReplay } from 'rxjs/operators';
import { MsTeamsModule } from '@app/modules/msteams/msteams.module';
import { Constants } from '@app/common/constants';

@Injectable({
  providedIn: MsTeamsModule
})
export class MsteamsService {

  isTeamsInitialized: boolean;
  authenticateWindowOpen = false;
  subject = new Subject<string>();
  queue = new Array<string>();
  authResultMap$ = new Map<string, Observable<string>>();

  constructor(@Inject(WINDOW) private window: Window) { }

  public initMsTeams(): Observable<void> {
    return new Observable<void>((subscriber) => {
      microsoftTeams.initialize(() => {
        this.isTeamsInitialized = true;
        this.notifyAppLoaded();
        this.notifyAppSuccess();
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  public notifyAppLoaded() {
    microsoftTeams.appInitialization.notifyAppLoaded();
  }

  public notifyAppSuccess() {
    microsoftTeams.appInitialization.notifySuccess();
  }

  public notifyAuthSuccess(result?: string) {
    microsoftTeams.authentication.notifySuccess(result);
  }

  public notifyAuthFailure() {
    microsoftTeams.authentication.notifyFailure();
  }

  public getContext(): Observable<microsoftTeams.Context> {
    const ctx = bindCallback(microsoftTeams.getContext);
    return ctx();
  }

  public authenticate(scope?: string): Observable<string> {
    const key = scope || 'none';
    // We will have lots of the same keys so we just return the replay of those.
    if (this.authResultMap$.has(key)) {
      return this.authResultMap$.get(key);
    } else {
      const authenticateUsingTeams$ = new Observable<string>(
        (subscriber) => {
          // Tell the service that auth window is open, we should not have multiple opened.
          this.authenticateWindowOpen = true;
          const params = {
            url: `${this.window.location.origin}/(auth:msteams-auth)?${Constants.IN_TEAMS_AUTH_POPUP_REDIRECT_KEY}=true&${Constants.SHOULD_NOT_LOAD_MAIN_KEY}=true${scope ? '&scope=' + scope : ''}`,
            successCallback: (result?: string) => {
              // Tell the service that the window is closed/done
              this.authenticateWindowOpen = false;
              subscriber.next(result);
              subscriber.complete();
            },
            failureCallback: (reason?: string) => {
              subscriber.error(reason);
            }
          } as microsoftTeams.authentication.AuthenticateParameters;

          microsoftTeams.authentication.authenticate(params);
        })
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
        );

      this.authResultMap$.set(key, authenticateUsingTeams$);

      return this.authResultMap$.get(key);
    }
  }
}
